// Relative imports (not `@/`) so bun can test this module.
import type { RuleSet } from '../rules';
import { isStarving, MILLI, type PlayerId, playerStock, type SimState, upkeepOf } from '../sim';
import {
  FOOD_WARN_SECONDS,
  type FoodSample,
  foodRate,
  pushFoodSample,
} from './notifications/derive/food';

/**
 * The resources pill's rates: each resource's net income per second (income
 * less upkeep and other drains, spending left out), measured the way the
 * food alert measures food (notifications/derive/food.ts: a sliding window
 * of stock samples, `foodRate`), and how long until soldiers start losing hp
 * for want of food. Pure, no React.
 */

/** Stock samples per resource, kept frame to frame. */
export type IncomeTracker = Map<string, FoodSample[]>;

export function createIncomeTracker(): IncomeTracker {
  return new Map();
}

/**
 * Samples the viewer's stock of each resource at `state.elapsed` and returns
 * each one's net rate per second (null until the window has filled enough).
 */
export function sampleIncome(
  tracker: IncomeTracker,
  state: SimState,
  rules: RuleSet,
  playerId: PlayerId
): Record<string, number | null> {
  const stock = playerStock(state, playerId);
  const upkeep = upkeepOf(state, rules, playerId);
  const rates: Record<string, number | null> = {};
  for (const res of Object.keys(rules.resources)) {
    let samples = tracker.get(res);
    if (!samples) {
      samples = [];
      tracker.set(res, samples);
    }
    pushFoodSample(samples, state.elapsed, (stock[res] ?? 0) / MILLI, upkeep[res] ?? 0);
    rates[res] = foodRate(samples);
  }
  return rates;
}

/**
 * Seconds until soldiers start losing hp (the stock running dry, then the
 * `starvationGrace` period), shown while that's under FOOD_WARN_SECONDS plus
 * the grace; null when food isn't running out or once they already are (the
 * starvation banner takes over). With the stock out and upkeep unpaid, the
 * grace period left.
 */
export function starvingIn(
  state: SimState,
  rules: RuleSet,
  playerId: PlayerId,
  foodPerSecond: number | null
): number | null {
  if (isStarving(state, rules, playerId)) return null;
  const grace = rules.pacing.starvationGrace;
  const food = (playerStock(state, playerId).food ?? 0) / MILLI;
  const since = state.players[playerId]?.starvingSince;
  if (food < 1 && since !== undefined) return Math.max(0, grace - (state.elapsed - since));
  if (foodPerSecond === null || foodPerSecond >= 0) return null;
  const empty = food / -foodPerSecond;
  return empty < FOOD_WARN_SECONDS ? empty + grace : null;
}

/** "+2.4/s", "−0.3/s", "0.0/s". */
export function rateLabel(perSecond: number): string {
  const r = Math.round(perSecond * 10) / 10;
  return `${r > 0 ? '+' : r < 0 ? '−' : ''}${Math.abs(r).toFixed(1)}/s`;
}
